// A customer's email in a sandboxed frame: it keeps its own design and cannot reach the dashboard.
import { useEffect, useMemo, useRef, useState } from 'react';
import { emailFrameDocument, sanitizeEmailHtml } from './email-html';

interface EmailHtmlContentProps {
  /** The stored email HTML. Renders nothing when empty. */
  html: string;
}

export function EmailHtmlContent({ html }: EmailHtmlContentProps): React.JSX.Element | null {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0);
  const srcDoc = useMemo(() => {
    const safeHtml = html.trim() ? sanitizeEmailHtml(html) : '';
    return safeHtml ? emailFrameDocument(safeHtml, getComputedStyle(document.body).fontFamily) : '';
  }, [html]);

  // Grows with the email, so the bubble shows all of it and the frame never scrolls.
  useEffect(() => {
    const element = frame.current;
    if (!element || !srcDoc) return;
    let observer: ResizeObserver | null = null;
    const fit = (): void => {
      const page = element.contentDocument?.documentElement;
      if (page) setHeight(page.scrollHeight);
    };
    const onLoad = (): void => {
      fit();
      const body = element.contentDocument?.body;
      if (body && typeof ResizeObserver !== 'undefined') {
        observer?.disconnect();
        observer = new ResizeObserver(fit);
        observer.observe(body);
      }
    };
    element.addEventListener('load', onLoad);
    return () => {
      element.removeEventListener('load', onLoad);
      observer?.disconnect();
    };
  }, [srcDoc]);

  if (!srcDoc) return null;
  return (
    <iframe
      ref={frame}
      title="Customer email"
      // No allow-scripts: nothing in the email runs; same origin only lets us read its height.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      style={{ height }}
      className="block w-full border-0"
    />
  );
}
